import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoursesPage({ user }) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await fetch(`${API}/courses`);
      if (response.ok) {
        setCourses(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(courses.map(c => c.category))];
  
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button data-testid="back-btn" onClick={() => navigate(user ? '/dashboard' : '/')} variant="ghost" className="rounded-full px-2 sm:px-4">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="text-lg sm:text-2xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>
              Browse Courses
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-12" data-testid="courses-page">
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                data-testid="search-courses-input"
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-full"
              />
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(category => (
              <Button
                key={category}
                data-testid={`category-${category}`}
                onClick={() => setSelectedCategory(category)}
                className={`rounded-full whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-[#0B0B0B] text-white'
                    : 'bg-white text-[#0B0B0B] border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading courses...</div>
        ) : filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No courses found matching your criteria</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(`/courses/${course.id}`)}
                className="cursor-pointer"
                data-testid={`course-card-${index}`}
              >
                <Card className="p-6 bg-white rounded-2xl hover:shadow-lg transition-all duration-300">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-[#0B0B0B]/60 mb-1">{course.category}</div>
                      <h3 className="text-xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>{course.title}</h3>
                    </div>
                    <div className="bg-[#D9C7A3] text-[#0B0B0B] px-3 py-1 rounded-full text-xs font-bold">{course.difficulty}</div>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm line-clamp-3">{course.description}</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-xs text-gray-500">Duration</div>
                      <div className="font-bold text-[#0B0B0B]">{course.duration_weeks} weeks</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Exercises</div>
                      <div className="font-bold text-[#0B0B0B]">{course.exercise_ids?.length || 0}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#0B0B0B]">R{course.price.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">{course.is_subscription ? '/month' : 'one-time'}</div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
